import { useMutation, useQuery } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { ChevronDown } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Temporal } from 'temporal-polyfill';
import { z } from 'zod';

import { InfoPage } from '@/components/info-page';
import { StudentBulkUpdate } from '@/components/students/student-bulk-update';
import { StudentStatusBadge } from '@/components/students/student-status-badge';
import { EntityTable, createTableRowSelectionCell } from '@/components/table/entity-table';
import { TableCellWithPopover } from '@/components/table/table-cell-with-popover';
import { TableErrorState } from '@/components/table/table-error-state';
import { TableFilterDropdown } from '@/components/table/table-filter-dropdown';
import { TableFilterInputField } from '@/components/table/table-filter-field';
import { TableFilterResetButton } from '@/components/table/table-filter-reset-button';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

import { EmptyArray, assignManualOnDemandGLA, getStudents } from '@/lib/api-calls';
import { buildClientExportColumns, exportToCSV } from '@/lib/csv-utils';
import { usePaginatedFilters } from '@/lib/filtering';
import { DEFAULT_PAGE_SIZE, paginationRequestSchema } from '@/lib/pagination';
import { generateQrCodesPdf } from '@/lib/qr-code-pdf-utils';
import { useNavigateSearchState, validateRouteRole } from '@/lib/routing';

import { useAllSchools, useOrgOptions, useSchoolAndOrgOptions } from '@/lib/api-call-hooks';
import { useRowsBulkSelection } from '@/lib/bulk-selection';
import { airServerErrorFrom, useErrorToast } from '@/lib/error-handling';
import { useSorting } from '@/lib/sorting';
import {
  calculateAttendanceRate,
  formatAttendedSessionDatesFromFeedback,
  getStudentStatusLabel,
  getStudentStatusOptions,
  getStudentYearOptions,
} from '@/lib/student-utils';
import { airColumnHelper } from '@/lib/tables';

import { useTimeZone } from '@/auth/useTimeZone';
import { StudentShortLink } from '@/components/join-now/student-short-link';
import { Tooltip } from '@/components/tooltip';
import { useToast } from '@/components/ui/hooks/use-toast';
import { getDistrictColumn } from '@/lib/district-utils';
import { CreateStudent } from '@/routes/_layout/users/-components/create-student';
import { CreateStudentsByCsv } from '@/routes/_layout/users/-components/create-students-by-csv';
import { formatPlainDate, getUpcomingSessions, isAirAdmin } from '@airreading/common';
import {
  FullStudent,
  GetStudentsSortableColumn,
  StudentStatus,
  StudentYearColorMap,
  getStudentsSortOrderSchema,
  getStudentsSortableColumnSchema,
  studentStatusSchema,
  studentYearOption,
  studentYearOptionOnlyNumber,
  studentYearSchema,
} from '@airreading/common/data';
import { studentsTabs } from './-components/tabs';
const studentsSearchSchema = paginationRequestSchema.extend({
  query: z.string().optional().catch(''),
  schools: z.array(z.string()).optional(),
  orgs: z.array(z.string()).optional(),
  grades: z.array(studentYearSchema).optional(),
  statuses: z.array(studentStatusSchema).optional(),
  sortBy: getStudentsSortOrderSchema.optional(),
});

export const Route = createFileRoute('/_layout/students/')({
  beforeLoad: validateRouteRole(isAirAdmin),
  component: StudentsIndex,
  validateSearch: studentsSearchSchema,
});

const col = airColumnHelper<FullStudent>(getStudentsSortableColumnSchema);

const buildColumns = (
  timeZone: string,
  schoolIdToDistrictName: Record<string, string>,
  isSelectAllPagesActive: boolean,
  onSelectAllPagesChange: (isSelectAllActive: boolean) => void,
  onAssignGLA: (studentId: string) => void,
) => [
  createTableRowSelectionCell(col, { isSelectAllPagesActive, onSelectAllPagesChange }),
  col.accessor((row) => row.id, {
    id: 'fullNameOrEmail',
    header: 'Name',
    cell: ({ row, getValue }) => (
      <Link
        to={'/students/$studentId'}
        params={{ studentId: getValue() }}
        search={{ pageIndex: 0, pageSize: DEFAULT_PAGE_SIZE }}
      >
        {row.original.fullNameOrEmail}
      </Link>
    ),
  }),
  col.accessor('shortCode', {
    header: 'Direct join short link',
    id: 'shortLink',
    cell: ({ getValue }) => {
      const shortLink = getValue();
      return <StudentShortLink shortCode={shortLink} />;
    },
  }),
  col.accessor('email', {
    id: 'email',
    header: 'Email',
  }),
  col.accessor('grade', {
    id: 'grade',
    header: 'Grade',
    cell: ({ getValue }) => {
      const grade = getValue();
      if (!grade) return studentYearOption(grade);
      const backgroundColor = StudentYearColorMap[grade];
      return (
        <div className="min-w-[80px] flex justify-start">
          <span className="text-xs rounded-full px-2 py-1 min-w-9 text-center" style={{ backgroundColor }}>
            {studentYearOption(grade)}
          </span>
        </div>
      );
    },
  }),
  col.accessor((row) => row, {
    id: 'school',
    header: 'School',
    enableSorting: false,
    cell: ({ getValue }) => {
      const school = getValue()?.school;
      return (
        school && (
          <Link to={'/orgs/schools/$schoolId/schedules'} params={{ schoolId: school.id }} className="text-blue-600">
            {school.name}
          </Link>
        )
      );
    },
  }),
  col.accessor('school', {
    id: 'district',
    header: 'District',
    cell: ({ getValue }) => {
      const school = getValue();
      const districtName = school?.id ? schoolIdToDistrictName[school.id] : null;
      if (!districtName) return <span>Unknown</span>;
      return school
        ? getDistrictColumn({
            orgId: school.orgId,
            orgName: districtName,
          })
        : null;
    },
  }),
  col.accessor('totalFutureSessions', {
    id: 'totalFutureSessions',
    header: 'Total future sessions',
    cell: ({ getValue }) => {
      const value = getValue();
      return value ?? 0;
    },
  }),
  col.accessor('nextSessionPlainDate', {
    header: 'Next session date',
    id: 'nextSessionPlainDate',
    cell: ({ getValue }) => {
      const date = getValue();
      return date === null ? '' : formatPlainDate(date);
    },
  }),
  col.accessor('studentSessions', {
    id: 'allFutureSessionDates',
    header: 'All future session dates',
    cell: ({ getValue }) => {
      const sessions = getValue() ?? [];
      const { futureSessions, todaySessions } = getUpcomingSessions({ sessions, timeZone });
      const allFutureSessions = [...todaySessions, ...futureSessions];

      if (allFutureSessions.length === 0) return '';

      const dates = allFutureSessions.map((s) => {
        const zonedDateTime = s.startInstant.toZonedDateTimeISO(timeZone);
        const month = String(zonedDateTime.month).padStart(2, '0');
        const day = String(zonedDateTime.day).padStart(2, '0');
        return `${month}/${day}`;
      });

      return <TableCellWithPopover items={dates} />;
    },
  }),
  col.accessor('studentSessions', {
    id: 'allFutureSessionLessons',
    header: 'All future session lessons',
    cell: ({ getValue }) => {
      const sessions = getValue() ?? [];
      const { futureSessions, todaySessions } = getUpcomingSessions({ sessions, timeZone });
      const allFutureSessions = [...todaySessions, ...futureSessions];

      // Map back to original sessions to get lesson property
      const sessionsMap = new Map(sessions.map((s) => [s.startInstant.toString(), s]));
      const sessionsWithLessons = allFutureSessions
        .map((s) => sessionsMap.get(s.startInstant.toString()))
        .filter((s): s is NonNullable<typeof s> => s !== undefined);

      if (sessionsWithLessons.length === 0) return '';

      const lessons = sessionsWithLessons
        .map((s) => (s.lesson ? s.lesson.name : null))
        .filter((name): name is string => name !== null);

      return <TableCellWithPopover items={lessons} />;
    },
  }),
  col.accessor('canLaunchManualGLA', {
    id: 'launchGLA',
    header: 'Launch GLA',
    cell: ({ getValue, row }) => {
      const canLaunch = getValue();
      const button = (
        <Button
          size="sm"
          disabled={!canLaunch}
          onClick={(e) => {
            e.stopPropagation();
            onAssignGLA(row.original.id);
          }}
        >
          Assign
        </Button>
      );
      if (canLaunch) return button;
      return <Tooltip trigger={button}>Not eligible</Tooltip>;
    },
  }),
  col.accessor('status', {
    id: 'status',
    header: 'Status',
    cell: ({ getValue }) => {
      return (
        <div className="flex gap-x-1">
          <StudentStatusBadge status={getValue()} />
        </div>
      );
    },
  }),
  col.accessor('studentSessions', {
    id: 'attendance-all-past-sessions',
    header: 'Attendance - all past sessions',
    cell: ({ row }) => {
      const sessionFeedbacks = row.original.sessionFeedbacks;
      return `${calculateAttendanceRate(sessionFeedbacks)}%`;
    },
  }),
  col.accessor('studentSessions', {
    id: 'attendance-all-past-sessions-last-14-days',
    header: 'Attendance - past 14 days',
    cell: ({ row }) => {
      const sessionFeedbacks = row.original.sessionFeedbacks;
      return `${calculateAttendanceRate(sessionFeedbacks, 14)}%`;
    },
  }),
  col.accessor('studentSessions', {
    id: 'attendance-all-past-sessions-number',
    header: 'Number of attendance - all past sessions',
    cell: ({ row }) => {
      const sessionFeedbacks = row.original.sessionFeedbacks;
      return (
        sessionFeedbacks?.filter((feedback) => feedback.studentAttended === true && feedback.status === 'completed')
          ?.length ?? 0
      );
    },
  }),
  col.accessor('studentSessions', {
    id: 'attendance-all-past-sessions-dates',
    header: 'All dates of attendance - all past sessions',
    cell: ({ row }) => {
      const sessionFeedbacks = row.original.sessionFeedbacks;
      const datesString = formatAttendedSessionDatesFromFeedback(sessionFeedbacks);
      if (!datesString) return '';

      const dates = datesString.split(', ').filter((date) => date.trim() !== '');
      return <TableCellWithPopover items={dates} />;
    },
  }),
  col.accessor('phonicsSkill1Mastered', {
    id: 'phonicsSkill1Mastered',
    header: 'Phonics skill 1 mastered',
    cell: ({ getValue }) => {
      const value = getValue();
      return value ?? 0;
    },
  }),
  col.accessor('phonicsSkill2Mastered', {
    id: 'phonicsSkill2Mastered',
    header: 'Phonics skill 2 mastered',
    cell: ({ getValue }) => {
      const value = getValue();
      return value ?? 0;
    },
  }),
  col.accessor('phonicsSkill3Mastered', {
    id: 'phonicsSkill3Mastered',
    header: 'Phonics skill 3 mastered',
    cell: ({ getValue }) => {
      const value = getValue();
      return value ?? 0;
    },
  }),
  col.accessor('morphemeSkill1Mastered', {
    id: 'morphemeSkill1Mastered',
    header: 'Morpheme skill 1 mastered',
    cell: ({ getValue }) => {
      const value = getValue();
      return value ?? 0;
    },
  }),
  col.accessor('morphemeSkill2Mastered', {
    id: 'morphemeSkill2Mastered',
    header: 'Morpheme skill 2 mastered',
    cell: ({ getValue }) => {
      const value = getValue();
      return value ?? 0;
    },
  }),
];

function StudentsIndex() {
  const { timeZone } = useTimeZone();
  const { data: allSchools } = useAllSchools();
  const { toast } = useToast();

  const schoolIdToDistrictName = useMemo(
    () => Object.fromEntries(allSchools.map((school) => [school.id, school.org.name])),
    [allSchools],
  );
  const [isSelectAllPagesActive, setIsSelectAllPagesActive] = useState(false);
  const [sortBy, didChangeSortBy] = useNavigateSearchState(Route.id, 'sortBy');
  // Default sort by student names alphabetically
  const effectiveSortBy = sortBy ?? ['fullNameOrEmail-asc'];
  const { sorting, ...tableSortingOptions } = useSorting<GetStudentsSortableColumn>([effectiveSortBy, didChangeSortBy]);

  const {
    pageIndex,
    pageSize,
    onPaginationChange,
    filters: {
      query: [[userNameQuery, activeUserNameQuery], setUserNameQuery],
      schools: [[selectedSchools, activeSelectedSchools], setSelectedSchools],
      orgs: [[selectedOrgs, activeSelectedOrgs], setSelectedOrgs],
      grades: [[selectedGrades, activeSelectedGrades], setSelectedGrades],
      statuses: [[selectedStatuses, activeSelectedStatuses], setSelectedStatuses],
    },
    resetFilters,
  } = usePaginatedFilters(Route.id, ['query', 'schools', 'orgs', 'grades', 'statuses']);

  const allOrganizationOptions = useOrgOptions();
  const allSchoolOptions = useSchoolAndOrgOptions({
    orgIds: activeSelectedOrgs ?? [],
  });
  const {
    data: response,
    isPending,
    error,
    refetch,
  } = useQuery(
    getStudents({
      pageIndex,
      pageSize,
      filters: {
        query: activeUserNameQuery,
        schools: activeSelectedSchools,
        orgs: activeSelectedOrgs,
        grades: activeSelectedGrades,
        statuses: activeSelectedStatuses,
      },
      sortBy: effectiveSortBy,
    }),
  );

  // Lazy query implementation - only executes when refetchAllStudents is called
  const { refetch: refetchAllStudents } = useQuery({
    ...getStudents({
      pageIndex: 0,
      pageSize: 0,
      filters: {
        query: activeUserNameQuery,
        schools: activeSelectedSchools,
        orgs: activeSelectedOrgs,
        grades: activeSelectedGrades,
        statuses: activeSelectedStatuses,
      },
      sortBy: effectiveSortBy,
      isAllPages: true,
    }),
    enabled: false, // This prevents automatic execution
  });

  const students = useMemo(() => response?.students || EmptyArray, [response]);
  useErrorToast(error, 'Error loading students. Please try again.');

  const { rowSelection, rowDataSelection, onRowSelectionChange, getRowId, resetAllRowSelection } =
    useRowsBulkSelection<FullStudent>({
      getRowId: (row) => row.id,
      rowIdToData: (id) => students.find((student) => student.id === id),
    });

  // Handle isSelectAllPagesActive changes and reset selections when deselecting all
  const handleSelectAllPagesChange = useCallback(
    (isActive: boolean) => {
      setIsSelectAllPagesActive(isActive);
      if (!isActive) {
        // When deselecting all pages, reset all row selections
        resetAllRowSelection();
      }
    },
    [resetAllRowSelection],
  );

  // Select all rows on current page when isSelectAllPagesActive is true and students change
  useEffect(() => {
    if (isSelectAllPagesActive && students.length > 0) {
      const allRowIds = students.reduce(
        (acc, student) => {
          acc[student.id] = true;
          return acc;
        },
        {} as Record<string, boolean>,
      );
      onRowSelectionChange(allRowIds);
    }
  }, [students, isSelectAllPagesActive, onRowSelectionChange]);

  const { mutate: assignGLA } = useMutation({
    mutationFn: assignManualOnDemandGLA,
    onSuccess: () => {
      toast({ title: 'On-demand GLA is assigned to the next session successfully' });
      refetch();
    },
    onError: (e) => {
      toast({ title: airServerErrorFrom(e)?.userFacingMessage || 'Failed to assign manual GLA' });
    },
  });

  const handleAssignGLA = useCallback((studentId: string) => assignGLA(studentId), [assignGLA]);

  // Build columns with select all pages support
  const columns = useMemo(
    () =>
      buildColumns(
        timeZone,
        schoolIdToDistrictName,
        isSelectAllPagesActive,
        handleSelectAllPagesChange,
        handleAssignGLA,
      ),
    [timeZone, schoolIdToDistrictName, isSelectAllPagesActive, handleSelectAllPagesChange, handleAssignGLA],
  );

  //get row count
  const totalRowCount = useMemo(() => response?.total || 0, [response]);

  // Reset isSelectAllPagesActive when filters change
  useEffect(() => {
    if (isSelectAllPagesActive) {
      setIsSelectAllPagesActive(false);
      resetAllRowSelection();
    }
  }, [
    activeUserNameQuery,
    activeSelectedSchools,
    activeSelectedOrgs,
    activeSelectedGrades,
    activeSelectedStatuses,
    isSelectAllPagesActive,
    resetAllRowSelection,
  ]);

  const onSuccess = () => {
    setIsSelectAllPagesActive(false);
    resetAllRowSelection();
    refetch();
  };

  // CSV Export functionality
  const exportColumns = useMemo(() => {
    const overrides = {
      fullNameOrEmail: (row: FullStudent) => row.fullNameOrEmail,
      grade: (row: FullStudent) => (row.grade ? studentYearOptionOnlyNumber(row.grade) : ''),
      school: (row: FullStudent) => row.school?.name || '',
      district: (row: FullStudent) => {
        const school = row.school;
        const districtName = school?.id ? schoolIdToDistrictName[school.id] : null;
        return districtName || 'Unknown';
      },
      nextSessionPlainDate: (row: FullStudent) => {
        const date = row.nextSessionPlainDate;
        return date === null ? '' : formatPlainDate(date);
      },
      status: (row: FullStudent) => getStudentStatusLabel(row.status),
      'attendance-all-past-sessions': (row: FullStudent) => {
        const rate = calculateAttendanceRate(row.sessionFeedbacks ?? []);
        return `${rate ?? 0}%`;
      },
      attendanceRate: (row: FullStudent) => {
        const rate = calculateAttendanceRate(row.sessionFeedbacks ?? []);
        return `${rate ?? 0}%`;
      },
      'attendance-all-past-sessions-last-14-days': (row: FullStudent) => {
        const rate = calculateAttendanceRate(row.sessionFeedbacks ?? [], 14);
        return `${rate ?? 0}%`;
      },
      'attendance-all-past-sessions-number': (row: FullStudent) => {
        const sessionFeedbacks = row.sessionFeedbacks ?? [];
        return sessionFeedbacks.filter(
          (feedback) => feedback.studentAttended === true && feedback.status === 'completed',
        ).length;
      },
      'attendance-all-past-sessions-dates': (row: FullStudent) => {
        return formatAttendedSessionDatesFromFeedback(row.sessionFeedbacks ?? []) || '';
      },
      lastSessionDate: (row: FullStudent) => {
        const dates = formatAttendedSessionDatesFromFeedback(row.sessionFeedbacks ?? []);
        return dates || '';
      },
      specialEd: (row: FullStudent) => (row.specialEd ? 'Yes' : 'No'),
      dyslexic: (row: FullStudent) => (row.dyslexic ? 'Yes' : 'No'),
      ell: (row: FullStudent) => (row.ell ? 'Yes' : 'No'),
      phonicsSkill1Mastered: (row: FullStudent) => row.phonicsSkill1Mastered ?? 0,
      phonicsSkill2Mastered: (row: FullStudent) => row.phonicsSkill2Mastered ?? 0,
      phonicsSkill3Mastered: (row: FullStudent) => row.phonicsSkill3Mastered ?? 0,
      morphemeSkill1Mastered: (row: FullStudent) => row.morphemeSkill1Mastered ?? 0,
      morphemeSkill2Mastered: (row: FullStudent) => row.morphemeSkill2Mastered ?? 0,
      morphemeSkill3Mastered: (row: FullStudent) => row.morphemeSkill3Mastered ?? 0,
    };
    return buildClientExportColumns(columns, overrides);
  }, [columns, schoolIdToDistrictName]);

  const table = useReactTable({
    data: students,
    columns,
    state: {
      pagination: {
        pageIndex,
        pageSize,
      },
      sorting,
      rowSelection,
    },
    rowCount: response?.total || 0,
    onRowSelectionChange,
    getRowId,
    getCoreRowModel: getCoreRowModel(),
    onPaginationChange,
    manualPagination: true,
    ...tableSortingOptions,
    enableColumnResizing: true,
  });

  useEffect(() => {
    if (!selectedSchools || selectedSchools.length === 0 || !selectedOrgs || selectedOrgs?.length === 0) return;

    const areAllSelectedSchoolsInSelectedDistricts = allSchoolOptions
      .filter((allSchool) => selectedSchools?.includes(allSchool.value))
      .map((item) => item.value);

    setSelectedSchools(areAllSelectedSchoolsInSelectedDistricts);
    if (areAllSelectedSchoolsInSelectedDistricts.length === selectedSchools.length) return;
    toast({
      title: 'The selected school was removed because it doesn’t belong to the chosen district.',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allSchoolOptions]);

  const filterLeftComponents = useMemo(
    () => (
      <>
        <TableFilterInputField
          placeholder="Search users by name or email"
          value={userNameQuery ?? ''}
          onChange={(event) => {
            setUserNameQuery(event.target.value);
          }}
        />
        <TableFilterDropdown
          title="Districts"
          options={allOrganizationOptions}
          selectedValues={selectedOrgs ?? []}
          onChange={setSelectedOrgs}
        />
        <TableFilterDropdown
          title="Schools"
          options={allSchoolOptions}
          selectedValues={selectedSchools ?? []}
          onChange={setSelectedSchools}
        />
        <TableFilterDropdown
          title="Grades"
          options={getStudentYearOptions()}
          selectedValues={selectedGrades ?? []}
          onChange={setSelectedGrades}
        />
        <TableFilterDropdown
          title="Status"
          options={getStudentStatusOptions()}
          selectedValues={selectedStatuses ?? []}
          onChange={setSelectedStatuses}
        />
        <TableFilterResetButton show={true} onClick={resetFilters} />
      </>
    ),
    [
      userNameQuery,
      setUserNameQuery,
      allSchoolOptions,
      selectedSchools,
      setSelectedSchools,
      allOrganizationOptions,
      selectedOrgs,
      setSelectedOrgs,
      selectedGrades,
      setSelectedGrades,
      selectedStatuses,
      setSelectedStatuses,
      resetFilters,
    ],
  );

  const bulkActionsMenuItems = (
    <>
      <DropdownMenuItem
        onSelect={async (e) => {
          e.preventDefault();
          const date = Temporal.Now.plainDateISO().toString();

          if (isSelectAllPagesActive) {
            // Export all students with filters applied (without pagination)
            const result = await refetchAllStudents();
            const allStudents = result.data?.students || [];
            exportToCSV(allStudents, exportColumns, `students_export_${date}.csv`);
          } else {
            // Export only selected rows from current page
            const selectedRows = table.getSelectedRowModel().rows.map((r) => r.original);
            exportToCSV(selectedRows, exportColumns, `students_export_${date}.csv`);
          }
        }}
      >
        Export students info
      </DropdownMenuItem>
      <StudentBulkUpdate
        confirmMessage="Are you sure you want to drop"
        invalidMessage="Only enrolled students can be selected."
        options={{ status: StudentStatus.dropped }}
        onSuccess={onSuccess}
        students={rowDataSelection}
        title="Mark drop off"
        validation={(student) => student.status === StudentStatus.enrolled}
        isSelectAllPagesActive={isSelectAllPagesActive}
        filters={{
          query: activeUserNameQuery,
          schools: activeSelectedSchools,
          orgs: activeSelectedOrgs,
          grades: activeSelectedGrades,
          statuses: activeSelectedStatuses,
        }}
        actionName="drop"
      />
      <StudentBulkUpdate
        confirmMessage="Are you sure you want to conclude the program for"
        invalidMessage="Only students with no program concluded status can be selected."
        options={{ status: StudentStatus.program_concluded }}
        onSuccess={onSuccess}
        students={rowDataSelection}
        title="Mark program concluded"
        validation={(student) => student.status !== StudentStatus.program_concluded}
        isSelectAllPagesActive={isSelectAllPagesActive}
        filters={{
          query: activeUserNameQuery,
          schools: activeSelectedSchools,
          orgs: activeSelectedOrgs,
          grades: activeSelectedGrades,
          statuses: activeSelectedStatuses,
        }}
        actionName="conclude"
      />
      <DropdownMenuItem
        onSelect={async (e) => {
          e.preventDefault();
          try {
            let studentsToExport: FullStudent[];

            if (isSelectAllPagesActive) {
              // Export all students with filters applied (without pagination)
              const result = await refetchAllStudents();
              studentsToExport = result.data?.students || [];
            } else {
              // Export only selected rows from current page
              studentsToExport = table.getSelectedRowModel().rows.map((r) => r.original);
            }

            await generateQrCodesPdf(studentsToExport);
            toast({
              title: 'QR codes PDF generated successfully',
            });
          } catch (error) {
            console.error('Failed to generate QR codes PDF:', error);
            toast({
              title: 'Failed to generate QR codes PDF',
              description: error instanceof Error ? error.message : 'An error occurred',
              variant: 'destructive',
            });
          }
        }}
      >
        Export QR code as PDF
      </DropdownMenuItem>
    </>
  );

  const topBarRightComponents = (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button className="ml-auto hidden h-8 lg:flex">
          Add students
          <ChevronDown className="mr-2 h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[150px]">
        <CreateStudent
          onSuccess={refetch}
          trigger={<DropdownMenuItem onSelect={(e) => e.preventDefault()}>Add a student</DropdownMenuItem>}
        />
        <CreateStudentsByCsv
          onSuccess={refetch}
          trigger={<DropdownMenuItem onSelect={(e) => e.preventDefault()}>Import from CSV</DropdownMenuItem>}
        />
      </DropdownMenuContent>
    </DropdownMenu>
  );
  return (
    <InfoPage headerText="Students" tabs={studentsTabs()}>
      <EntityTable
        table={table}
        showPagination={true}
        paginationOptions={[10, 20, 30, 40, 50, 100]}
        isLoading={isPending}
        hasError={!!error}
        emptyState={'No students found'}
        errorState={<TableErrorState refetchAction={refetch} />}
        filters={{ filterLeftComponents }}
        bulkActionsMenuItems={bulkActionsMenuItems}
        topBarRightComponents={topBarRightComponents}
        isSelectAllPagesActive={isSelectAllPagesActive}
        onSelectAllPagesChange={handleSelectAllPagesChange}
        totalRowCount={totalRowCount}
      />
    </InfoPage>
  );
}
